import React from 'react';

import { classnames } from '@lumapps/classnames';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { ATTRIBUTE_TYPOGRAPHY } from '@lumapps/lumx-blocks/constants';
import { RelativeDate } from '@lumapps/lumx-texts/components/RelativeDate';
import { mdiCalendar } from '@lumapps/lumx/icons';
import { ColorPalette, ColorVariant, Size } from '@lumapps/lumx/react';
import { makeSecuredMediaURLRelative } from '@lumapps/medias/utils';
import { useTranslate } from '@lumapps/translations';
import { WIDGET_EVENT_METADATA } from '@lumapps/widget-event-metadata/keys';

import { useEventBlock } from '../../hooks/useEventBlock';
import { Event } from '../../types';

export interface EventBlockProps {
    className?: string;
    event: Event;
    isDisabled?: boolean;
    variant?: string;
}
const CLASSNAME = 'event-block';

/**
 * Block that displays an event in the context of an event picker.
 *
 * @param EventBlockProps
 * @returns EventBlock
 */
export const EventBlock: React.FC<EventBlockProps> = ({ className, event, isDisabled, variant }) => {
    const { author, createdAt, image, formatedTimezone, title, formatedStartDate, formatedEndDate } =
        useEventBlock(event);
    const { translateAndReplace } = useTranslate();
    const isSmallVariant = variant === 'small';

    return (
        <GenericEntityBlock
            title={title}
            className={classnames(className, CLASSNAME)}
            isDisabled={isDisabled}
            thumbnail={{
                alt: image?.alt || '',
                image: makeSecuredMediaURLRelative(image?.url) as string,
                icon: mdiCalendar,
                size: isSmallVariant ? Size.m : Size.l,
            }}
            metadata={
                <GenericEntityBlock.Attribute
                    key="date"
                    value={`${translateAndReplace(WIDGET_EVENT_METADATA.DATE_TIME_VALUE, {
                        start: formatedStartDate,
                        end: formatedEndDate,
                    })} (${formatedTimezone})`}
                />
            }
            additionalInfo={
                !isSmallVariant
                    ? [
                          <GenericEntityBlock.User key="author" {...author} />,
                          <RelativeDate
                              key="date"
                              date={createdAt}
                              textProps={{
                                  typography: ATTRIBUTE_TYPOGRAPHY,
                                  color: ColorPalette.dark,
                                  colorVariant: ColorVariant.L2,
                              }}
                          />,
                      ]
                    : undefined
            }
        />
    );
};

EventBlock.displayName = 'EventBlock';
